/**
 * WidgetCaptureOverlay — fullscreen slide-up sheet for "+" capture.
 *
 * Mirrors `.add-overlay` from /Users/neil/Downloads/unified-widget-shell.html
 * (lines 298-308): translateY(100% → 0), cubic-bezier(0.32, 0.72, 0, 1),
 * ~92% white background + 40px backdrop blur. Top-rounded (44px) so it
 * peels up out of the dashboard frame.
 *
 * Image picker uses `accept="image/*"` WITHOUT the `capture` attribute —
 * the playground NEVER opens a camera (feedback_playground_never_camera).
 * Uploads ride api.uploadDataUrl so the GCS URL shape stays in lockstep
 * with the chat composer.
 *
 * Send → dispatchExec({ prompt: text, mediaUrls: [url], context: {
 *   widget_id, widget_use_contract: { widget_id, widget_instance_id } } }).
 * The backend pins the new session to THIS instance so the runner can hand the
 * agent the bound `instance_<id>` ref for mcp_session_log_to_widget (logging) /
 * mcp_widget_update_instance (rename) — the agent never invents the id.
 * Passing the instance id (not just widget_id) is required for template
 * instances — collections — which the backend will not auto-instantiate.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, Send } from 'lucide-react';
import { api } from '../../services/api';
import { describeApiError } from '../../services/apiError';
import { stripHiddenTags, composeHiddenSendPrompt } from '../../utils/text';

// Reference shell token set (lines 9-17 of unified-widget-shell.html).
const T = {
  bg: '#F2F2F7',
  card: '#FFFFFF',
  text: '#1C1C1E',
  textMut: '#8E8E93',
  cell: '#E5E5EA',
  accent: '#9A9589',
  accentLight: '#F0EFEA',
  healthGreen: '#34C759',
  warn: '#FF9500',
  rCard: '28px',
  rPill: '100px',
  rTile: '14px',
};

const FONT_UI = "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif";

export default function WidgetCaptureOverlay(props) {
  // Each opened source owns its asynchronous work. A canceled picker or an
  // old upload must never fill (or auto-send from) another capture visit.
  const source = JSON.stringify([
    props.open, props.widgetId, props.instanceId, props.continueSessionId,
    props.initialNote, props.initialImageUrl, props.initialImages || [], props.quick,
    globalThis.window?.__RUNTIME_API_URL__ || '',
  ]);
  return <WidgetCaptureSession key={source} {...props} />;
}

function WidgetCaptureSession({
  open,
  onClose,
  instanceId, // the per-user widget instance this dashboard is showing
  widgetId, // parent widget id (for SKILL.md loading on the runner side)
  widgetTitle,
  hint, // optional placeholder pulled from widget metadata / SKILL.md
  initialImageUrl, // optional GCS url pre-filled by the widget "+" bridge
  initialNote, // optional note text pre-filled by the widget (open_capture data-prompt)
  continueSessionId, // optional session to CONTINUE (open_capture payload.session_id) —
  // a session-bound board's "+" keeps the capture UX but appends into the
  // board's rolling session instead of minting a new session per add
  // Background-dispatch lifecycle (Canon Common-UX #12, app parity): when
  // onDispatchStarted is wired, the host renders the transient pending row
  // immediately. Keep inputs in the sheet until the server confirms a Session
  // so failed requests remain editable and retryable.
  onDispatchStarted,
  onDispatchResolved,
  onDispatchFailed,
  // Depop-style quick add (open_capture payload.quick, app parity): the pick
  // IS the confirmation. The sheet shrinks to the photo tile only (no note,
  // no AI banner, no Send) and dispatches the moment the upload lands; the
  // widget renders the in-slot "Removing background…" tile meanwhile.
  quick = false,
  // Scripted send (open_capture `images`): stored `media://` refs the widget
  // pre-attached (e.g. My Wardrobe's model photo). When non-empty the overlay
  // does a HEADLESS send — no picker, no composer — dispatching with these refs
  // already attached, then closes (mirrors the app; app-capability tier 0.8.2).
  initialImages = [],
}) {
  const [text, setText] = useState('');
  const [image, setImage] = useState(null); // { url, name, isVideo }
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const fileRef = useRef(null);
  const recognitionRef = useRef(null);
  const alive = useRef(false);
  const sendLock = useRef(false);
  const uploadOwner = useRef(0);
  const uploadAbort = useRef(null);
  useEffect(() => {
    alive.current = true;
    uploadAbort.current = new AbortController();
    return () => {
      alive.current = false;
      uploadAbort.current?.abort();
      try { recognitionRef.current?.stop(); } catch { /* already stopped */ }
    };
  }, []);

  // Adopt an externally-uploaded image (from the widget "+" bridge) so the
  // capture sheet opens already filled in and the user only has to confirm.
  useEffect(() => {
    if (!open) return;
    if (initialImageUrl && (!image || image.url !== initialImageUrl)) {
      setImage({ url: initialImageUrl, name: 'widget-photo' });
    }
  }, [open, initialImageUrl, image]);

  // Adopt a widget-scripted note (open_capture data-prompt) on open — only
  // into an empty input so the user's own typing is never clobbered. Hidden
  // <vi_prefix>/<vi_suffix> instruction blocks are stripped from the seeded
  // text (the user only sees the visible part); they are re-attached on send.
  useEffect(() => {
    if (!open || !initialNote) return;
    const visible = stripHiddenTags(initialNote);
    setText((prev) => (prev ? prev : visible));
  }, [open, initialNote]);

  // Reset state when the overlay closes so re-opening is a fresh sheet.
  useEffect(() => {
    if (!open) {
      setText('');
      setImage(null);
      setError(null);
      setUploading(false);
      setSending(false);
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch { /* ignore */ }
      }
      setListening(false);
    }
  }, [open]);

  // Quick add: pop the file picker as soon as the sheet opens (best-effort —
  // browsers may require the tap on the hero tile when user activation
  // doesn't survive the iframe postMessage hop).
  useEffect(() => {
    if (!open || !quick) return;
    try { fileRef.current?.click(); } catch { /* fall through to the tile */ }
  }, [open, quick]);

  // Web Speech API toggle — appends recognized text into the note input.
  const onToggleMic = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError('Voice input unavailable in this browser. Type your note instead.');
      return;
    }
    if (listening && recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
      return;
    }
    const recog = new SpeechRecognition();
    recog.continuous = false;
    recog.interimResults = true;
    recog.lang = (navigator.language || 'en-US');
    let interim = '';
    recog.onresult = (evt) => {
      let finalText = '';
      interim = '';
      for (let i = evt.resultIndex; i < evt.results.length; i++) {
        const r = evt.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interim += r[0].transcript;
      }
      if (finalText) {
        setText((prev) => (prev ? prev + ' ' + finalText.trim() : finalText.trim()));
      }
    };
    recog.onerror = (evt) => {
      setListening(false);
      if (evt?.error && evt.error !== 'no-speech' && evt.error !== 'aborted') {
        setError(`Voice: ${evt.error}`);
      }
    };
    recog.onend = () => {
      setListening(false);
      if (interim) setText((prev) => (prev ? prev + ' ' + interim.trim() : interim.trim()));
    };
    recognitionRef.current = recog;
    try {
      recog.start();
      setListening(true);
      setError(null);
    } catch (e) {
      setError(`Voice: ${e?.message || e}`);
    }
  }, [listening]);

  const onPickFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || sendLock.current) return;
    const owner = ++uploadOwner.current;
    const ownsUpload = () => alive.current && owner === uploadOwner.current;
    setError(null);
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      if (!ownsUpload()) return;
      const url = await api.uploadDataUrl(dataUrl, { signal: uploadAbort.current.signal });
      if (!ownsUpload()) return;
      if (!url) throw new Error('Upload did not return a photo');
      setImage({ url, name: file.name });
    } catch (err) {
      if (ownsUpload()) setError(`Upload failed: ${describeApiError(err)}`);
    } finally {
      if (ownsUpload()) setUploading(false);
    }
  }, []);

  const onSendRef = useRef(null);
  // Quick add: the uploaded pick auto-sends — no Send tap.
  useEffect(() => {
    if (!open || !quick || !image || uploading || sending || error) return;
    onSendRef.current?.();
  }, [open, quick, image, uploading, sending, error]);

  // Scripted send (open_capture `images`): the widget pre-attached a stored
  // photo, so dispatch headlessly on open — no picker, no Send tap. Fire once
  // per open (a ref guard); the setTimeout lets the initialNote text settle so
  // the composed prompt carries the visible summary + hidden blocks.
  const scriptedFiredRef = useRef(false);
  const scripted = Array.isArray(initialImages) && initialImages.length > 0;
  useEffect(() => {
    if (!open) { scriptedFiredRef.current = false; return undefined; }
    if (!scripted || scriptedFiredRef.current) return undefined;
    const id = setTimeout(() => {
      if (scriptedFiredRef.current) return;
      scriptedFiredRef.current = true;
      onSendRef.current?.();
    }, 0);
    return () => clearTimeout(id);
  }, [open, scripted]);

  const onSend = useCallback(async () => {
    if (!alive.current || sendLock.current || sending || uploading) return;
    if (!image && !text.trim() && !scripted) {
      setError('Add a photo or a note before sending.');
      return;
    }
    sendLock.current = true;
    setSending(true);
    setError(null);
    // Deterministic session title: "<widget_title> YYYY-MM-DD HH:mm"
    // so the History list shows a stable, sortable label instead of
    // the user prompt ("log this meal" — collides across captures)
    // or the default backend "Session Task" fallback.
    const now = new Date();
    const two = (n) => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}-${two(now.getMonth() + 1)}-${two(now.getDate())} ${two(now.getHours())}:${two(now.getMinutes())}`;
    const titleStr = (widgetTitle || '').trim() || 'Capture';
    const sessionTitle = `${titleStr} ${stamp}`;
    // Re-attach the hidden <vi_prefix>/<vi_suffix> blocks from the seed prompt
    // around the visible text — the model gets the full instruction, the stored
    // message keeps the markers (display strips them everywhere).
    // A scripted send may have no typed text yet — fall back to the visible part
    // of the widget's prompt so the composed instruction still carries it.
    const visibleText = text.trim() || (scripted ? stripHiddenTags(initialNote || '') : '');
    const sendPrompt = composeHiddenSendPrompt(initialNote, visibleText);
    const doDispatch = (clientRef) => api.dispatchExec({
      prompt: sendPrompt || ' ', // backend rejects pure-empty prompt; one space keeps it valid
      // Pre-attached stored `media://` refs (scripted send) — the ref IS the
      // input handle the backend resolves; no upload.
      mediaUrls: image ? [image.url] : (scripted ? initialImages : undefined),
      // Continue the widget-designated session (bound board "+") instead of
      // minting a new one; absent → new session, as before.
      sessionId: continueSessionId || undefined,
      title: sessionTitle,
      context: (instanceId || widgetId) ? {
        // Primary v07 marker — backend pins the session to this widget
        // instance so the runner hands the agent the bound instance ref for
        // mcp_session_log_to_widget / mcp_widget_update_instance, without the
        // LLM inventing the id. The instance id is required for template instances
        // (collections), which the backend will not auto-instantiate from a
        // bare widget_id.
        widget_id: widgetId,
        widget_use_contract: {
          widget_id: widgetId,
          widget_instance_id: instanceId,
        },
        // Persists as session context.widget_client_ref and echoes back on
        // the widget_data_changed SSE — the host resolves the EXACT
        // placeholder it drew even when this response is lost.
        ...(clientRef ? { widget_client_ref: clientRef } : {}),
      } : (clientRef ? { widget_client_ref: clientRef } : undefined),
    });
    const background = typeof onDispatchStarted === 'function';
    const tempId = background ? `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` : null;
    if (background) {
      const pendingThumb = image?.url || (scripted
        ? initialImages.find((value) => typeof value === 'string' && value)
        : null);
      onDispatchStarted(tempId, sessionTitle, pendingThumb || null);
    }
    try {
      const res = await doDispatch(tempId);
      if (!alive.current) return;
      const sessionId = String(res?.sessionId || res?.session_id || '').trim();
      if (!sessionId) throw new Error('The server did not return a Session ID');
      if (background) onDispatchResolved?.(tempId, sessionId);
      onClose?.({ dispatched: true, sessionId });
    } catch (err) {
      if (!alive.current) return;
      sendLock.current = false;
      if (background) onDispatchFailed?.(tempId, err);
      setError(`Send failed: ${describeApiError(err)}`);
    } finally {
      if (alive.current) setSending(false);
    }
  }, [sending, uploading, image, text, scripted, initialImages, initialNote, instanceId, widgetId, widgetTitle, continueSessionId, onClose, onDispatchStarted, onDispatchResolved, onDispatchFailed]);
  onSendRef.current = onSend;

  return (
    <div
      aria-hidden={!open}
      style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',
        zIndex: 90,
        transform: open ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
        overflowY: 'auto',
        borderRadius: '44px 44px 0 0',
        fontFamily: FONT_UI,
        color: T.text,
        // The dashboard's main column is the relative anchor — the overlay
        // covers it fully but does NOT cover the playground sidebar. That's
        // intentional: the user can bail by clicking another tab.
      }}
    >
      {/* Sticky header — cancel + title only. Send moved to the input bar
          so the primary action lives next to the text users are typing.
          Spacer balances the title's centered alignment. */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px 10px',
          position: 'sticky', top: 0, zIndex: 2,
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '0.5px solid rgba(0,0,0,0.06)',
        }}
      >
        <button
          onClick={() => onClose?.()}
          aria-label="Cancel"
          style={{
            width: 32, height: 32, borderRadius: '50%',
            background: '#F2F2F7', color: '#1C1C1E',
            border: 'none', cursor: 'pointer',
            fontSize: 20, fontWeight: 300,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          ×
        </button>
        <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.3px', color: '#1C1C1E' }}>
          {quick ? 'Add item' : (hint?.title || (widgetTitle ? `Log to ${widgetTitle}` : 'Quick capture'))}
        </div>
        <div style={{ width: 32 }} aria-hidden="true" />
      </div>

      <div style={{ padding: '10px 20px 40px' }}>
        {/* Hero camera placeholder — mirrors `.add-hero-cam` + `.cam-placeholder`
            in the reference (lines 328-339). Tap anywhere on the card to open
            the local file picker. NO `capture` attribute — playground never
            opens the camera (memory: feedback_playground_never_camera). */}
        <div style={{ marginBottom: 16 }}>
          {image ? (
            <div
              style={{
                position: 'relative', borderRadius: 20, overflow: 'hidden',
                background: '#F2F2F7', border: '1px solid rgba(0,0,0,0.04)',
              }}
            >
              <img
                src={image.url}
                alt={image.name}
                style={{ width: '100%', maxHeight: 360, objectFit: 'cover', display: 'block' }}
              />
              <button
                onClick={() => setImage(null)}
                disabled={sending}
                aria-label="Remove photo"
                style={{
                  position: 'absolute', top: 10, right: 10,
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.55)', color: '#fff',
                  border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, backdropFilter: 'blur(4px)',
                }}
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              data-widget-capture-hero
              onClick={() => fileRef.current?.click()}
              disabled={uploading || sending}
              style={{
                width: '100%',
                background: '#F2F2F7', border: '1px solid rgba(0,0,0,0.04)',
                borderRadius: 20, padding: '36px 20px', textAlign: 'center',
                cursor: uploading ? 'wait' : 'pointer', color: '#1C1C1E',
                fontFamily: FONT_UI,
              }}
            >
              <div
                style={{
                  width: 72, height: 72, borderRadius: '50%',
                  border: '2px dashed rgba(0,0,0,0.12)',
                  margin: '0 auto 12px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 32, color: '#1C1C1E',
                }}
              >
                {uploading ? '…' : '📸'}
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#1C1C1E' }}>
                {uploading ? 'Uploading…' : (quick ? 'Choose a photo' : (hint?.heroLabel || 'Take a photo'))}
              </div>
              <div style={{ fontSize: 13, color: '#8E8E93', marginTop: 4 }}>
                {quick ? 'Background removed automatically — it lands on the board' : (hint?.heroSub || 'AI will read the photo and log it for this widget')}
              </div>
            </button>
          )}
          <input
            ref={fileRef}
            data-testid="widget-capture-file"
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={onPickFile}
          />
        </div>

        {/* AI note banner — mirrors `.add-ai-note` (lines 380-386).
            Hidden in quick mode: the pick is the whole interaction. */}
        {!quick && <div
          style={{
            display: 'flex', gap: 10, alignItems: 'flex-start', padding: 14,
            background: '#F2F2F7', borderRadius: 14, marginTop: 14,
            border: '0.5px solid rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>✦</div>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: '#1C1C1E', fontWeight: 500 }}>
            {hint?.aiNote || 'AI will interpret your photo + note and append a structured entry to this widget.'}
          </div>
        </div>}

        {/* Quick mode: dispatch status instead of the composer. */}
        {quick && !error && (uploading || sending || image) && (
          <div
            style={{
              display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center',
              padding: 14, background: '#F2F2F7', borderRadius: 14, marginTop: 14,
              fontSize: 13, fontWeight: 600, color: '#1C1C1E',
            }}
          >
            ✂ Adding to the board…
          </div>
        )}

        {/* Composer row — mic LEFT (Web Speech API toggle, dictates into the
            note), input MIDDLE, send button RIGHT. Mirrors the iPhone
            _CaptureOverlay row so growth-testing parity is byte-for-byte
            consistent across surfaces. Hidden in quick mode (auto-send). */}
        {!quick && <div
          data-testid="widget-capture-composer"
          style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 16 }}
        >
          <button
            onClick={onToggleMic}
            disabled={sending}
            aria-label={listening ? 'Stop voice input' : 'Start voice input'}
            title={listening ? 'Stop voice input' : 'Voice input (Web Speech)'}
            style={{
              width: 44, height: 44, borderRadius: 12,
              background: listening ? '#1C1C1E' : '#F2F2F7',
              color: listening ? '#fff' : '#1C1C1E',
              border: 'none', cursor: 'pointer', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: listening ? '0 0 0 4px rgba(28,28,30,0.15)' : 'none',
              transition: 'background 0.15s ease, box-shadow 0.15s ease',
            }}
          >
            <Mic size={18} strokeWidth={2.2} />
          </button>
          <input
            type="text"
            value={text}
            disabled={sending}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !sending) onSend(); }}
            placeholder={hint?.notePlaceholder || 'Add a note (optional)'}
            style={{
              flex: 1, padding: '14px 16px',
              border: '1.5px solid #E5E5EA', borderRadius: 12,
              fontSize: 14, fontWeight: 500, color: '#1C1C1E', outline: 'none',
              fontFamily: 'inherit', background: '#fff',
            }}
          />
          <button
            data-testid="widget-capture-send"
            onClick={onSend}
            disabled={sending || uploading || (!image && !text.trim() && !scripted)}
            aria-busy={sending || undefined}
            aria-label="Send"
            style={{
              width: 44, height: 44, borderRadius: 12,
              background: (sending || uploading || (!image && !text.trim() && !scripted)) ? '#E5E5EA' : '#1C1C1E',
              color: (sending || uploading || (!image && !text.trim() && !scripted)) ? '#8E8E93' : '#fff',
              border: 'none', flexShrink: 0,
              cursor: (sending || uploading || (!image && !text.trim() && !scripted)) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s ease',
            }}
          >
            <Send size={18} strokeWidth={2.2} />
          </button>
        </div>}

        {error && (
          <div
            style={{
              marginTop: 12, padding: 12, background: '#FFF1F0',
              border: '1px solid #FFD0CC', color: '#B3261E',
              borderRadius: 14, fontSize: 12,
            }}
          >
            {error}
            {quick && (image || scripted) && <button type="button" onClick={onSend} disabled={sending || uploading}
              style={{ display: 'block', minHeight: 44, marginTop: 8, border: '1px solid currentColor', borderRadius: 12, padding: '8px 16px', background: 'transparent', color: 'inherit', font: 'inherit' }}>Try again</button>}
          </div>
        )}
      </div>
    </div>
  );
}
